import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  Search, Eye, Edit, Trash2, ChevronLeft, ChevronRight,
  RefreshCw, Filter, Users, ArrowLeft, ArrowRight
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const ALL_COLS = [
  { key: 'rc_number', label: 'RC Number' },
  { key: 'name', label: 'Name' },
  { key: 'surname', label: 'Surname' },
  { key: 'id_number', label: 'ID Number' },
  { key: 'telephone', label: 'Telephone' },
  { key: 'gender', label: 'Gender' },
  { key: 'age', label: 'Age' },
  { key: 'municipality', label: 'Municipality' },
  { key: 'place_of_work', label: 'Place of Work' },
  { key: 'union_name', label: 'Union' },
  { key: 'emp_no', label: 'Emp No.' },
  { key: 'dob', label: 'Date of Birth' },
  { key: 'client_number', label: 'Client No.' },
  { key: 'email', label: 'Email' },
];

const EDIT_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'surname', label: 'Surname' },
  { key: 'id_number', label: 'ID Number' },
  { key: 'telephone', label: 'Telephone' },
  { key: 'gender', label: 'Gender' },
  { key: 'municipality', label: 'Municipality' },
  { key: 'place_of_work', label: 'Place of Work' },
  { key: 'emp_no', label: 'Emp No.' },
  { key: 'dob', label: 'Date of Birth' },
];

export const AdminUsers = () => {
  const { adminApi } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage] = useState(25);

  const [genderFilter, setGenderFilter] = useState('');
  const [municipalityFilter, setMunicipalityFilter] = useState('');
  const [categories, setCategories] = useState({ genders: [], municipalities: [] });
  const [showFilters, setShowFilters] = useState(false);

  const [viewMember, setViewMember] = useState(null);
  const [editMember, setEditMember] = useState(null);
  const [editData, setEditData] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tableRef = React.useRef(null);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
        search,
        ...(genderFilter && { gender: genderFilter }),
        ...(municipalityFilter && { municipality: municipalityFilter }),
      });
      const res = await adminApi.get(`/admin/all-members?${params}`);
      setMembers(res.data.members);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (err) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [adminApi, page, perPage, search, genderFilter, municipalityFilter]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await adminApi.get('/admin/samwu/categories');
        setCategories(res.data);
      } catch (e) { /* ignore */ }
    };
    loadCategories();
  }, [adminApi]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const scrollTable = (dir) => {
    if (tableRef.current) {
      tableRef.current.scrollBy({ left: dir === 'right' ? 300 : -300, behavior: 'smooth' });
    }
  };

  const handleView = (member) => setViewMember(member);

  const handleEditOpen = (member) => {
    setEditMember(member);
    setEditData({ ...member });
  };

  const handleEditSave = async () => {
    try {
      const src = editMember.source;
      const rawId = editMember.id.replace(`${src}_`, '');
      if (src === 'samwu') {
        await adminApi.put(`/admin/samwu/members/${rawId}`, editData);
      } else {
        await adminApi.put(`/admin/users/${rawId}`, editData);
      }
      toast.success('Member updated');
      setEditMember(null);
      fetchMembers();
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async () => {
    try {
      const src = deleteConfirm.source;
      const rawId = deleteConfirm.id.replace(`${src}_`, '');
      if (src === 'samwu') {
        await adminApi.delete(`/admin/samwu/members/${rawId}`);
      } else {
        await adminApi.delete(`/admin/users/${rawId}`);
      }
      toast.success('Member deleted');
      setDeleteConfirm(null);
      fetchMembers();
    } catch { toast.error('Failed to delete'); }
  };

  if (viewMember) {
    return (
      <div className="space-y-6" data-testid="member-detail-view">
        <Button onClick={() => setViewMember(null)} variant="ghost" className="text-gray-600 hover:text-gray-900">
          <ChevronLeft className="w-5 h-5 mr-2" /> Back to Members
        </Button>
        <Card className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-afriperx-green/10 flex items-center justify-center">
                <span className="text-afriperx-gold font-bold text-xl">{viewMember.name?.charAt(0)}{viewMember.surname?.charAt(0)}</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 font-syne">{viewMember.name} {viewMember.surname}</h2>
                <p className="text-gray-500 text-sm">RC: {viewMember.rc_number || 'N/A'} | Source: {viewMember.source}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => { setViewMember(null); handleEditOpen(viewMember); }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="edit-member-btn">
                <Edit className="w-4 h-4 mr-2" /> Edit
              </Button>
              <Button onClick={() => { setViewMember(null); setDeleteConfirm(viewMember); }} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="delete-member-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </Button>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {ALL_COLS.map(f => (
              <div key={f.key} className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500 text-xs mb-1">{f.label}</p>
                <p className="text-gray-900 font-medium">{viewMember[f.key] || 'N/A'}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-syne text-2xl font-bold text-gray-900">Total Users</h1>
          <p className="text-gray-500 text-sm">{total.toLocaleString()} total members</p>
        </div>
        <Button onClick={fetchMembers} variant="outline" size="sm" className="border-gray-300 text-gray-600" data-testid="refresh-users-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      <div className="flex gap-3 flex-wrap">
        <form onSubmit={handleSearch} className="flex-1 min-w-[240px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search name, ID, telephone, RC number..."
            className="pl-10 bg-white border-gray-300 text-gray-900"
            data-testid="search-users-input"
          />
        </form>
        <Button onClick={() => setShowFilters(!showFilters)} variant="outline" className="border-gray-300 text-gray-600" data-testid="filter-users-btn">
          <Filter className="w-4 h-4 mr-1" /> Filters
        </Button>
      </div>

      {showFilters && (
        <Card className="bg-white border border-gray-200 p-4 rounded-xl">
          <div className="flex gap-4 flex-wrap items-end">
            <div>
              <Label className="text-gray-600 text-xs">Gender</Label>
              <select value={genderFilter} onChange={(e) => { setGenderFilter(e.target.value); setPage(1); }} className="mt-1 block w-40 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900" data-testid="filter-gender">
                <option value="">All</option>
                {categories.genders?.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-gray-600 text-xs">Municipality</Label>
              <select value={municipalityFilter} onChange={(e) => { setMunicipalityFilter(e.target.value); setPage(1); }} className="mt-1 block w-56 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900" data-testid="filter-municipality">
                <option value="">All</option>
                {categories.municipalities?.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <Button variant="ghost" size="sm" onClick={() => { setGenderFilter(''); setMunicipalityFilter(''); setPage(1); }} className="text-gray-500">Clear</Button>
          </div>
        </Card>
      )}

      {/* Table with scroll arrows */}
      <Card className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-afriperx-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div className="relative">
              <div ref={tableRef} className="overflow-x-auto" data-testid="users-table-scroll">
                <table className="w-max min-w-full" data-testid="users-table">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {ALL_COLS.map(col => (
                        <th key={col.key} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">
                          {col.label}
                        </th>
                      ))}
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap sticky right-0 bg-gray-50 z-10 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {members.length === 0 ? (
                      <tr><td colSpan={ALL_COLS.length + 1} className="text-center py-12 text-gray-400">No members found</td></tr>
                    ) : (
                      members.map(m => (
                        <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                          {ALL_COLS.map(col => (
                            <td key={col.key} className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap max-w-[200px] truncate">
                              {m[col.key] || '-'}
                            </td>
                          ))}
                          <td className="px-4 py-3 sticky right-0 bg-white z-10 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)]">
                            <div className="flex items-center gap-1">
                              <Button onClick={() => handleView(m)} variant="ghost" size="sm" className="text-blue-600 hover:bg-blue-50" data-testid={`view-${m.id}`}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button onClick={() => handleEditOpen(m)} variant="ghost" size="sm" className="text-amber-600 hover:bg-amber-50" data-testid={`edit-${m.id}`}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button onClick={() => setDeleteConfirm(m)} variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" data-testid={`delete-${m.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Scroll arrows + Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2">
                <Button onClick={() => scrollTable('left')} variant="outline" size="sm" className="border-gray-300" data-testid="scroll-left-btn">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <span className="text-xs text-gray-400">Scroll columns</span>
                <Button onClick={() => scrollTable('right')} variant="outline" size="sm" className="border-gray-300" data-testid="scroll-right-btn">
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-sm text-gray-500">Page {page} of {totalPages} ({total.toLocaleString()} members)</p>
              <div className="flex gap-2">
                <Button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} variant="outline" size="sm" className="border-gray-300" data-testid="prev-page-btn">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  let pn;
                  if (totalPages <= 5) pn = i + 1;
                  else if (page <= 3) pn = i + 1;
                  else if (page >= totalPages - 2) pn = totalPages - 4 + i;
                  else pn = page - 2 + i;
                  return (
                    <Button key={pn} onClick={() => setPage(pn)} variant={page === pn ? 'default' : 'outline'} size="sm" className={page === pn ? 'bg-afriperx-green text-white' : 'border-gray-300 text-gray-600'}>
                      {pn}
                    </Button>
                  );
                })}
                <Button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} variant="outline" size="sm" className="border-gray-300" data-testid="next-page-btn">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editMember} onOpenChange={() => setEditMember(null)}>
        <DialogContent className="bg-white border-gray-200 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-gray-900 font-syne">Edit Member</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              {EDIT_FIELDS.map(f => (
                <div key={f.key}>
                  <Label className="text-gray-600 text-xs">{f.label}</Label>
                  <Input value={editData[f.key] || ''} onChange={(e) => setEditData({ ...editData, [f.key]: e.target.value })} className="mt-1 bg-white border-gray-300 text-gray-900 text-sm" />
                </div>
              ))}
            </div>
            <Button onClick={handleEditSave} className="w-full bg-afriperx-green hover:bg-afriperx-green-dark text-white" data-testid="save-edit-btn">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-white border-gray-200 max-w-sm">
          <DialogHeader><DialogTitle className="text-gray-900 font-syne">Confirm Delete</DialogTitle></DialogHeader>
          <p className="text-gray-600 text-sm py-2">Delete <strong>{deleteConfirm?.name} {deleteConfirm?.surname}</strong>? This cannot be undone.</p>
          <div className="flex gap-3 justify-end">
            <Button onClick={() => setDeleteConfirm(null)} variant="outline" className="border-gray-300">Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-afriperx-green-dark text-white" data-testid="confirm-delete-btn">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
